import React, { createContext, useState, useEffect } from "react";
import {
  lightningAPI,
  lightningScenarioAPI,
  lassieDashboardAPI,
  lightningUserActionAPI,
  organizationAPI,
  inspectionAPI
} from "../api";
import { windfarmTimeZone } from "../prettyDate";

export const LassieContext = createContext();

export default function LassieProvider({ children }) {
  const oneHour = 60 * 60 * 1000;
  const freq = "daily";
  const [windfarms, setWindfarms] = useState([]);
  const [selectedWindfarm, setSelectedWindfarm] = useState(null);
  const [wfStatistics, setWfStatistics] = useState([]);
  const [turbines, setTurbines] = useState([]);
  const [turbinesStatus, setTurbinesStatus] = useState({
    noRisk: [],
    atRisk: [],
    toBeInspected: [],
  });
  const [loading, setLoading] = useState(true);
  const [turbinesLoading, setTurbinesLoading] = useState(true)
  const [scenarios, setScenarios] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [historicActivity, setHistoricActivity] = useState([]);
  const [historicUserAction, setHistoricUserAction] = useState([]);
  const [historicTriggers, setHistoricTriggers] = useState([]);
  const [scenarioStatistics, setScenarioStatistics] = useState([]);
  const [windfarmStatistics, setWindfarmStatistics] = useState([]);
  const [seasonalStatistics, setSeasonalStatistics] = useState([]);
  const [turbineStatistics, setTurbineStatistics] = useState([]);
  const [dashboardDateRange, setDashboardDateRange] = useState([])
  const [notificationsOverview, setNotificationsOverview] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [organization, setOrganization] = useState([])
  const [users, setUsers] = useState([])
  const [organizationWindfarms, setOrganizationWindfarms] = useState([])
  const [inspectionTemplates, setInspectionTemplates] = useState([])
  const [selectedComment, setSelectedComment] = useState(null)
  const [updateComment, setUpdateComment] = useState({ show: false, is_new_comment: true })
  const [refetchUserActionsFlag, setRefetchUserActionsFlag] = useState(0);
  const [organizationLoading, setOrganizationLoading] = useState({
    organization: false,
    users: false,
    wdUsers: false,
    windfarms: false,
    invitation: false,
    roleUpdate: {},
    notification: {},
    templates: false,
    dueDates: false
  });

  const [organizationPopup, setOrganizationPopup] = useState({
    show: false,
    status: '',
    title: '',
    buttonText: '',
    buttonStatus: '',
    action: null,
    text: '',
    buttonText2: '',
    buttonStatus2: '',
    action2: null,
    target_user_id: null,
    link: '',
    updated_role: null,
    is_due_date: false,
  });

  const [turbineModels, setTurbineModels] = useState([])
  const [notificationUpdate, setNotificationUpdate] = useState({ id: null, status: false });
  const [notificationsLoading, setNotificationsLoading] = useState({});
  const [activeNotificationWindfarmID, setActiveNotificationWindfarmID] = useState(null);
  const [selectedUserID, setSelectedUserID] = useState(null);
  const [showAddUserPopup, setShowAddUserPopup] = useState(false);
  const [newDueDate, setNewDueDate] = useState({ unit: 'Days', value: 0 });
  const [selectedTurbineIds, setSelectedTurbineIds] = useState([]);
  const [editMode, setEditMode] = useState({ id: null, status: false });
  const [selectedTurbine, setSelectedTurbine] = useState(null);
  const [mapPopUpLoading, setMapPopUpLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  const [layerSettings, setLayerSettings] = useState({
    background: "topographic",
    view: "Lightning",
    turbines: true,
    lightning: false,
    collection_area: false,
    heatmap: false,
    turbine_specific: [],
    most_probable: false,
    only_show_outside_iec: false,
  });



  const logError = (message, error) => {
    console.error(message, error);
  };

  const sortTurbines = (turbines) => {
    if (!turbines) return;
    return turbines.sort(
      (a, b) => b.probability_of_atleast_one - a.probability_of_atleast_one
    );
  };

  const triggerRefetchUserActions = () => {
    setRefetchUserActionsFlag(prev => prev + 1);
  };

  const fetchWindfarms = async () => {
    const storedWindfarms = JSON.parse(localStorage.getItem("windfarms"));
    const lastFetched = localStorage.getItem("windfarmsTimestamp");

    if (storedWindfarms && Date.now() - lastFetched < oneHour) {
      const sortedWindfarms = storedWindfarms.sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      setWindfarms(sortedWindfarms);
      setLoading(false);
    } else {
      try {
        const data = await lightningAPI.listLightningWindfarms();
        const sortedData = data.sort((a, b) => a.name.localeCompare(b.name));
        setWindfarms(sortedData);
        localStorage.setItem("windfarms", JSON.stringify(sortedData));
        localStorage.setItem("windfarmsTimestamp", Date.now());
      } catch (error) {
        console.error("Error fetching windfarm data", error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const handleLoad = () => {
      localStorage.removeItem("windfarms");
      localStorage.removeItem("windfarmsTimestamp");
    };

    window.addEventListener("load", handleLoad);
    return () => {
      window.removeEventListener("load", handleLoad);
    };
  }, []);

  const fetchStatistics = async (windfarm_id, max_distance_km) => {
    try {
      const stats = await lightningAPI.getStatistics(windfarm_id, max_distance_km);
      setWfStatistics(stats);
    } catch (error) {
      logError(`Error fetching statistics for windfarm ${windfarm_id}`, error);
    }
  };

  const fetchTurbines = async (windfarm_id, turbine_id, max_distance_km) => {
    setTurbinesLoading(true)
    try {
      const allTurbines = await lightningAPI.listAllLightningTurbines(
        windfarm_id,
        turbine_id,
        max_distance_km
      );
      const sortedTurbines = sortTurbines(allTurbines);

      const filterTurbinesByStatus = (status) =>
        sortedTurbines.filter((t) => t.status === status);

      setTurbines(sortedTurbines);
      setTurbinesStatus({
        noRisk: filterTurbinesByStatus("acceptable_risk"),
        atRisk: filterTurbinesByStatus("at_risk"),
        toBeInspected: filterTurbinesByStatus("marked_for_inspection"),
      });
    } catch (error) {
      logError(`Error fetching turbines for windfarm ${windfarm_id}`, error);
    } finally {
      setTurbinesLoading(false)
    }
  };

  const fetchScenarios = async (windfarm_id) => {
    try {
      const scenariosData =
        await lightningScenarioAPI.list_scenarios_by_windfarm_id(windfarm_id);
      setScenarios(scenariosData);
    } catch (error) {
      logError(`Error fetching scenarios for windfarm ${windfarm_id}`, error);
    }
  };

  const fetchNotifications = async (windfarm_id) => {
    try {
      const notificationsData =
        await lassieDashboardAPI.listNotificationOverview(windfarm_id);
      setNotifications(notificationsData);
    } catch (error) {
      logError(
        `Error fetching notifications for windfarm ${windfarm_id}`,
        error
      );
    }
  };

  const fetchHistoricActivity = async (
    windfarm_id,
    from_timestamp,
    to_timestamp
  ) => {
    try {
      const historicActivityData =
        await lassieDashboardAPI.listHistoricActivity({
          windfarm_id,
          from_timestamp,
          to_timestamp,
        });
      setHistoricActivity(historicActivityData);
    } catch (error) {
      logError(
        `Error fetching historic activity for windfarm ${windfarm_id}`,
        error
      );
    }
  };

  const fetchHistoricUserAction = async (
    windfarm_id
  ) => {
    try {
      const historicUserActionsData =
        await lassieDashboardAPI.listWindfarmsHistoricUserAction(windfarm_id);
      setHistoricUserAction(historicUserActionsData);

    } catch (error) {
      logError(
        `Error fetching historic user actions for windfarm ${windfarm_id}`,
        error
      );
    }
  };

  const fetchHistoricTriggers = async (
    windfarm_id,
    from_timestamp,
    to_timestamp
  ) => {
    try {
      const historicTriggersData =
        await lassieDashboardAPI.listHistoricTriggers(
          windfarm_id,
          from_timestamp,
          to_timestamp
        );
      setHistoricTriggers(historicTriggersData);
    } catch (error) {
      logError(
        `Error fetching historic triggers for windfarm ${windfarm_id}`,
        error
      );
    }
  };

  const formattedAction = (action) => {
    if (action === "marked_for_inspection") {
      return 'Comment';
    } else if (action === "acceptable_risk") {
      return 'Risk accepted';
    }
    return action;
  };

  const getFeedbackMessage = (action, success = true) => {
    switch (action) {
      case "marked_for_inspection":
        return success ? "Comment added successfully" : "Adding comment failed";
      case "acceptable_risk":
        return success ? "Risk accepted successfully" : "Risk acceptance failed";
      default:
        return success ? `${formattedAction(action)} added successfully` : `${formattedAction(action)} failed`;
    }
  };


  const createUserAction = async ({
    turbine_ids,
    action,
    user_comment,
    target_date,
    file_names,
    file_datas,
    due_in_hours,
    inspection_type,
    inspection_method,
  }) => {
    setMapPopUpLoading(true)

    try {
      const updatedTurbines = await lightningUserActionAPI.insert_user_action(
        turbine_ids,
        action,
        user_comment,
        target_date,
        file_names,
        file_datas,
        due_in_hours,
        inspection_type,
        inspection_method,
      );

      setTurbines((prevTurbines) =>
        prevTurbines.map((turbine) =>
          updatedTurbines.some((updated) => updated.id === turbine.id)
            ? updatedTurbines.find((updated) => updated.id === turbine.id)
            : turbine
        )
      );
      triggerRefetchUserActions();
      setFeedback({
        type: "success", message: getFeedbackMessage(action, true),
      });

    } catch (error) {
      console.error("Error creating user action:", error);
      setFeedback({
        type: "error", message: getFeedbackMessage(action, false),
      });
    } finally {
      setMapPopUpLoading(false)
    }
  };


  const updateTurbinesAndSelected = (updatedTurbines) => {
    setTurbines(prevTurbines =>
      prevTurbines.map(turbine =>
        updatedTurbines.some(updated => updated.id === turbine.id)
          ? updatedTurbines.find(updated => updated.id === turbine.id)
          : turbine
      )
    );

    // The logbook map is drawn from historicActivity, so it needs the new inspection state too.
    // Only the action state is taken over: the strikes and risk belong to the period shown.
    setHistoricActivity(prev =>
      prev?.map(turbine => {
        const updated = updatedTurbines.find(u => u.id === turbine.id);
        return updated
          ? { ...turbine, inspection: updated.inspection, marked_for_repair: updated.marked_for_repair }
          : turbine;
      })
    );

    if (selectedTurbine) {
      const updated = updatedTurbines.find(t => t.id === selectedTurbine.id);
      if (updated) {
        setSelectedTurbine(updated);
      }
    }
  };


  const handleResolveInspection = async ({ turbine_ids, inspection_ids, inspection_date, user_comment }) => {
    setMapPopUpLoading(true)
    try {
      const updatedTurbines = await inspectionAPI.resolveInspections(turbine_ids, inspection_ids, inspection_date, user_comment);
      updateTurbinesAndSelected(updatedTurbines);
      triggerRefetchUserActions();
      setFeedback({ type: "success", message: "Inspection resolved successfully!" });
      return updatedTurbines;
    } catch (error) {
      console.error("Error resolving inspections:", error);
      setFeedback({ type: "error", message: "Failed to resolve inspection" });
    }
    finally {
      setMapPopUpLoading(false)
    }
  };

  const handleScheduleInspections = async ({ turbine_ids, inspection_date, user_comment, inspection_type, inspection_method }) => {
    setMapPopUpLoading(true)
    try {
      // The notification email shows when the inspection was scheduled in the wind farm's local time
      const time_zone = windfarmTimeZone(turbines.filter(t => turbine_ids.includes(t.id)));
      const updatedTurbines = await inspectionAPI.scheduleInspections(turbine_ids, inspection_date, { user_comment, inspection_type, inspection_method, time_zone });
      updateTurbinesAndSelected(updatedTurbines);
      triggerRefetchUserActions();
      setFeedback({ type: "success", message: "Inspection scheduled successfully!" });
      return updatedTurbines;
    } catch (error) {
      console.error("Error scheduling inspections:", error);
      setFeedback({ type: "error", message: "Failed to schedule inspection" });
    } finally {
      setMapPopUpLoading(false)
    }
  };

  const handleRejectInspections = async ({ turbine_ids, inspection_ids, inspection_date, user_comment }) => {
    setMapPopUpLoading(true)
    try {
      const updatedTurbines = await inspectionAPI.rejectInspections(turbine_ids, inspection_ids, inspection_date, user_comment);
      updateTurbinesAndSelected(updatedTurbines);
      triggerRefetchUserActions();
      setFeedback({ type: "success", message: "Inspection rejected successfully!" });
      return updatedTurbines;
    } catch (error) {
      console.error("Error rejecting inspections:", error);
      setFeedback({ type: "error", message: "Failed to reject inspection" });
    } finally {
      setMapPopUpLoading(false)
    }
  };

  const handleUploadInspectionData = async ({ turbine_id, inspection_id, file_names, file_datas, target_date, user_comment }) => {
    setMapPopUpLoading(true)
    try {
      const updatedTurbines = await inspectionAPI.uploadInspectionData(
        turbine_id,
        inspection_id,
        file_names,
        file_datas,
        target_date, user_comment
      );
      updateTurbinesAndSelected(updatedTurbines);
      triggerRefetchUserActions();
      setFeedback({ type: "success", message: "The documents was uploaded successfully!" });

      return updatedTurbines;
    } catch (error) {
      console.error("Error uploading inspection data:", error);
      setFeedback({ type: "error", message: "`Failed to upload documents." });
    }
    finally {
      setMapPopUpLoading(false)
    }
  };

  const handleCreateDisposition = async ({ turbine_id, inspection_id, defect_id, user_comment }) => {
    setMapPopUpLoading(true)
    try {
      const updatedTurbines = await inspectionAPI.createDisposition({ turbine_id, inspection_id, defect_id, user_comment });
      updateTurbinesAndSelected(updatedTurbines);
      triggerRefetchUserActions();
      setFeedback({ type: "success", message: "Defect created successfully!" });

      return updatedTurbines;
    } catch (error) {
      console.error("Error creating disposition:", error);
      setFeedback({ type: "error", message: "Failed to create defect" });
    } finally {
      setMapPopUpLoading(false)
    }
  };

  const fetchDashboardDateRange = async (windfarm_id) => {
    try {
      setDashboardDateRange([])

      const dateRange = await lassieDashboardAPI.dashboardDateRange(windfarm_id)
      setDashboardDateRange(dateRange)
    } catch (error) {
      console.error("Error fetching statistics data:", error);
      throw error;
    }
  }

  const fetchStatisticsData = async (startDate, endDate, windfarm_id) => {
    try {
      setWindfarmStatistics([])
      const windfarmData = await lassieDashboardAPI.listStatistics(startDate, endDate, windfarm_id)
      setWindfarmStatistics(windfarmData)
    } catch (error) {
      console.error("Error fetching statistics data:", error);
      throw error;
    }
  };


  const fetchScenarioStatistics = async (startDate, endDate, windfarm_id) => {
    try {
      setScenarioStatistics([])
      const scenarioData = await lassieDashboardAPI.listScenarioStatistics(startDate, endDate, windfarm_id)
      setScenarioStatistics(scenarioData);
    } catch (error) {
      console.error("Error fetching scenario data:", error);
      throw error;
    }
  }

  const fetchSeasonalStatistics = async (windfarm_id) => {
    try {
      setSeasonalStatistics([])
      const seasonalData = await lassieDashboardAPI.listSeasonalStatistics(
        windfarm_id
      );
      setSeasonalStatistics(seasonalData);
    } catch (error) {
      console.error("Error fetching seasonal data:", error);
      throw error;
    }
  };

  const fetchTurbineStatistics = async (startDate, endDate, windfarm_id) => {
    try {
      setTurbineStatistics([]);

      const turbineData = await lassieDashboardAPI.listTurbineStatistics(
        startDate,
        endDate,
        windfarm_id
      );
      setTurbineStatistics(turbineData);
    } catch (error) {
      console.error("Error fetching turbine statistics:", error);
      throw error;
    }
  };

  const fetchScenariosOverview = async () => {
    setSettingsLoading(true);
    try {
      const data = await lightningScenarioAPI.scenarios_overview();
      setNotificationsOverview(data);
    } catch (error) {
      console.log(error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const subscribeToScenario = async (scenario, freq) => {
    try {
      await lightningScenarioAPI.subscribe_to_scenario(
        scenario.scenario_id,
        freq
      );
    } catch (error) {
      console.error(error);
    }
  };

  const unsubscribeToScenario = async (scenario, freq) => {
    try {
      await lightningScenarioAPI.unsubscribe_from_scenario(
        scenario.scenario_id,
        freq
      );
    } catch (error) {
      console.error(error);
    }
  };

  const updateScenarioStatus = async (scenario, windfarm_id) => {
    setNotificationUpdate({ id: scenario.scenario_id, status: true })
    try {
      const newSubscriptionStatus = !scenario.isSubscribed;

      if (newSubscriptionStatus) {
        await subscribeToScenario(scenario, freq);
      } else {
        await unsubscribeToScenario(scenario, freq);
      }

      setNotificationsOverview((prevOverview) => {
        if (!prevOverview[windfarm_id]) {
          console.log("none");
          return prevOverview;
        }
        return {
          ...prevOverview,
          [windfarm_id]: prevOverview[windfarm_id].map((n) =>
            n.scenario_id === scenario.scenario_id
              ? { ...n, isSubscribed: newSubscriptionStatus }
              : n
          ),
        };
      });
    } catch (error) {
      console.error("Error updating scenario status:", error);
    } finally {
      setNotificationUpdate({ id: null, status: false })

    }
  };


  const fetchOrganizations = async () => {
    setOrganizationLoading(prev => ({ ...prev, organization: true }));
    try {
      const organizationData = await organizationAPI.getOrganizations();
      setOrganization(organizationData);
    } catch (error) {
      console.log(
        `Error fetching organizations data: ${error}`);
    } finally {
      setOrganizationLoading(prev => ({ ...prev, organization: false }));

    }
  };

  const fetchOrganizationUsers = async (organization) => {
    setOrganizationLoading(prev => ({ ...prev, users: true }));
    try {
      const organizationUsers = await organizationAPI.getOrganizationUsers(organization.id);
      setUsers(organizationUsers)
    } catch (error) {
      console.log(
        `Error fetching organization users data: ${error}`);
    } finally {
      setOrganizationLoading(prev => ({ ...prev, users: false }));
    }
  };

  const fetchOrganizationWindfarms = async (organization) => {
    setOrganizationLoading(prev => ({ ...prev, windfarms: true }));
    try {
      const organizationWindfarms = await organizationAPI.getOrganizationWindfarms(organization.id);
      setOrganizationWindfarms(organizationWindfarms)
    } catch (error) {
      console.log(
        `Error fetching organization windfarms data: ${error}`);
    } finally {
      setOrganizationLoading(prev => ({ ...prev, windfarms: false }));
    }
  }
  const fetchTurbineModels = async (windfarm_id) => {
    try {
      const turbineModels = await lightningScenarioAPI.listTurbineModels(windfarm_id)
      setTurbineModels(turbineModels)
    } catch (error) {
      console.error("Error fetching turbine models:", error);
    }
  }


  const getInspectionTemplates = async (organization_id) => {
    setOrganizationLoading(prev => ({ ...prev, templates: true }));

    try {
      const it = await organizationAPI.listInspectionTemplates(organization_id)
      setInspectionTemplates(it)
    } catch (error) {
      console.log(`Error fetching inspection templates: ${error}`);
    } finally {
      setOrganizationLoading(prev => ({ ...prev, templates: false }));
    }
  }

  const subscribeUserToNotification = async (scenario_id, frequency, target_user_id) => {
    setOrganizationLoading(prev => ({ ...prev, notification: { ...prev.notification, [scenario_id]: true } }));
    try {
      const response = await lightningScenarioAPI.createScenarioUserSubscription(
        scenario_id,
        frequency,
        target_user_id
      );
      if (response && response.id) {
        updateUserNotificationState(target_user_id, scenario_id, true);
      }
      return response;
    } catch (error) {
      console.error(`Error subscribing user to notification: ${error}`);
    } finally {
      setOrganizationLoading(prev => ({ ...prev, notification: { ...prev.notification, [scenario_id]: false } }));
    }
  };

  const unsubscribeUserFromNotification = async (scenario_id, frequency, target_user_id) => {
    setOrganizationLoading(prev => ({ ...prev, notification: { ...prev.notification, [scenario_id]: true } }));
    try {
      const response = await lightningScenarioAPI.deleteScenarioUserSubscription(
        scenario_id,
        frequency,
        target_user_id
      );
      if (response === 'OK') {
        updateUserNotificationState(target_user_id, scenario_id, false);
      }
      return response;
    } catch (error) {
      console.error(`Error unsubscribing user from notification: ${error}`);
    } finally {
      setOrganizationLoading(prev => ({ ...prev, notification: { ...prev.notification, [scenario_id]: false } }));
    }
  };


  const updateUserNotificationState = (target_user_id, scenario_id, isSubscribed) => {
    setUsers(prevUsers =>
      prevUsers.map(user =>
        user.id === target_user_id
          ? {
            ...user,
            windfarms: user.windfarms.map(wf =>
              wf.id === activeNotificationWindfarmID
                ? {
                  ...wf,
                  scenarios: wf.scenarios.map(notification =>
                    notification.id === scenario_id
                      ? { ...notification, subscribed: isSubscribed }
                      : notification
                  )
                } : wf
            )
          } : user
      )
    );
  };

  const createInspectionTemplate = async (organization_id, title, comment, due_in_hours, overdue_remind_in_days, inspection_type, inspection_method) => {
    setOrganizationLoading(prev => ({ ...prev, templates: true }));
    try {
      const newTemplate = await organizationAPI.createInspectionTemplate(organization_id, title, comment, { due_in_hours, overdue_remind_in_days, inspection_type, inspection_method });
      if (newTemplate) {
        setInspectionTemplates(prev => [...prev, newTemplate]);
      }
    } catch (error) {
      console.log(`Error creating comment template: ${error}`);
      setOrganizationPopup({
        show: true,
        status: 'warning',
        title: 'Something went wrong',
        buttonText: 'Close',
        buttonStatus: 'warning',
        text: error.response?.data
      });
    }
    finally {
      setOrganizationLoading(prev => ({ ...prev, templates: false }));

    }
  };

  useEffect(() => {
    fetchWindfarms();
    setTurbines([]);

    const intervalId = setInterval(fetchWindfarms, oneHour);
    return () => clearInterval(intervalId);
  }, [selectedWindfarm]);

  useEffect(() => {
    setTurbinesStatus({
      noRisk: turbines.filter((t) => t.status === "acceptable_risk"),
      atRisk: turbines.filter((t) => t.status === "at_risk"),
      toBeInspected: turbines.filter(
        (t) => t.status === "marked_for_inspection"
      ),
    });
  }, [turbines]);

  function formatDueInHours(hours) {
    if (hours == null || hours === 0) return "No due date";

    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;

    let result = 'Due in ';
    if (days > 0) result += `${days} day${days !== 1 ? 's' : ''}`;
    if (days > 0 && remainingHours > 0) result += ', ';
    if (remainingHours > 0) result += `${remainingHours} hour${remainingHours !== 1 ? 's' : ''}`;

    return result;
  }

  return (
    <LassieContext.Provider
      value={{
        windfarms,
        setWindfarms,
        selectedWindfarm,
        setSelectedWindfarm,
        loading,
        setLoading,
        wfStatistics,
        setWfStatistics,
        fetchStatistics,
        fetchTurbines,
        turbines,
        turbinesStatus,
        fetchScenarios,
        scenarios,
        setScenarios,
        fetchNotifications,
        notifications,
        setNotifications,
        fetchHistoricActivity,
        historicActivity,
        setHistoricActivity,
        fetchHistoricUserAction,
        historicUserAction,
        setHistoricUserAction,
        fetchHistoricTriggers,
        historicTriggers,
        setHistoricTriggers,
        createUserAction,
        fetchStatisticsData,
        scenarioStatistics,
        setScenarioStatistics,
        windfarmStatistics,
        setWindfarmStatistics,
        fetchSeasonalStatistics,
        seasonalStatistics,
        setSeasonalStatistics,
        fetchTurbineStatistics,
        turbineStatistics,
        setTurbineStatistics,
        notificationsOverview,
        setNotificationsOverview,
        settingsLoading,
        setSettingsLoading,
        fetchScenariosOverview,
        subscribeToScenario,
        unsubscribeToScenario,
        updateScenarioStatus,
        notificationUpdate,
        setNotificationUpdate,
        dashboardDateRange,
        setDashboardDateRange,
        fetchDashboardDateRange,
        fetchScenarioStatistics,
        turbinesLoading,
        setTurbinesLoading,
        fetchOrganizations,
        fetchOrganizationUsers,
        fetchOrganizationWindfarms,
        organization,
        setOrganization,
        organizationLoading,
        setOrganizationLoading,
        users,
        setUsers,
        organizationWindfarms,
        setOrganizationWindfarms,
        fetchTurbineModels,
        turbineModels,
        setTurbineModels,
        notificationsLoading,
        setNotificationsLoading,
        inspectionTemplates,
        setInspectionTemplates,
        getInspectionTemplates,
        organizationPopup,
        setOrganizationPopup,
        subscribeUserToNotification,
        unsubscribeUserFromNotification,
        activeNotificationWindfarmID,
        setActiveNotificationWindfarmID,
        selectedUserID,
        setSelectedUserID,
        showAddUserPopup,
        setShowAddUserPopup,
        newDueDate,
        setNewDueDate,
        formatDueInHours,
        selectedTurbineIds,
        setSelectedTurbineIds,
        selectedComment,
        setSelectedComment,
        updateComment,
        setUpdateComment,
        createInspectionTemplate,
        editMode,
        setEditMode,
        handleResolveInspection,
        handleScheduleInspections,
        handleRejectInspections,
        handleUploadInspectionData,
        handleCreateDisposition,
        selectedTurbine,
        setSelectedTurbine,
        mapPopUpLoading,
        setMapPopUpLoading,
        refetchUserActionsFlag,
        setRefetchUserActionsFlag,
        refetchUserActionsFlag,
        triggerRefetchUserActions,
        feedback,
        setFeedback,
        layerSettings,
        setLayerSettings
      }}
    >
      {children}
    </LassieContext.Provider>
  );
}
